import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import { VERSION } from '../version';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, getUserName } = useAuth();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup' | 'confirm'>('signin');
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Debug logging for Header
  console.log('🔍 Header - User object:', user);
  console.log('🔍 Header - Is user authenticated?', !!user);

  // Helper function to get first name
  const getFirstName = (): string => {
    const fullName = getUserName();
    if (!fullName) return 'User';
    return fullName.split(' ')[0];
  };

  // Helper function to get initials
  const getInitials = (): string => {
    const name = getUserName();
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleSignIn = () => {
    setAuthMode('signin');
    setIsAuthModalOpen(true);
  };

  const handleSignOut = async () => {
    await signOut();
    setIsProfileDropdownOpen(false);
  };

  const handleCreateTrip = () => {
    navigate('/create-trip');
    setIsMenuOpen(false);
  };

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return (
    <>
      {/* Mobile Header - New Design */}
      {isMobile ? (
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#EAEFED]" style={{ paddingTop: '20px', paddingBottom: '12px' }}>
          <div className="flex items-center justify-between px-4 h-12">
            {/* Left - Menu Button */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="w-11 h-11 rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.04)] flex items-center justify-center transition-all hover:shadow-[0_4px_8px_rgba(0,0,0,0.06)]"
                aria-label="Menu"
              >
                <svg className="w-6 h-6 text-[#0B1412]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {/* Menu Dropdown */}
              {isMenuOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-[0_10px_18px_rgba(0,0,0,0.08)] border border-[#E3E8E5] py-2 z-50">
                  {/* Trip Planner Header */}
                  <div className="px-4 py-3 border-b border-[#EAEFED]">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary-700 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-lg">✈️</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-[#0B1412]">Trip Planner</h3>
                        <p className="text-xs text-[#6F7974]">v{VERSION}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Menu Links */}
                  <div className="py-2">
                    <Link
                      to="/trips"
                      onClick={() => setIsMenuOpen(false)}
                      className={`block px-4 py-3 text-[#0B1412] hover:bg-[#F2FBF9] transition-colors ${
                        isActive('/trips') || isActive('/') ? 'bg-[#F2FBF9] font-semibold' : ''
                      }`}
                    >
                      My Trips
                    </Link>
                    <button
                      onClick={handleCreateTrip}
                      className="w-full text-left px-4 py-3 text-[#0B1412] hover:bg-[#F2FBF9] transition-colors"
                    >
                      Create Trip
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right - Notifications and Profile */}
            <div className="flex items-center gap-3">
              {/* Notifications Button */}
              <button
                className="relative w-11 h-11 rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.04)] flex items-center justify-center transition-all hover:shadow-[0_4px_8px_rgba(0,0,0,0.06)]"
                aria-label="Notifications"
              >
                <svg className="w-5 h-5 text-[#0B1412]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span className="absolute -top-1 -right-1 w-4.5 h-4.5 bg-[#E05A4D] rounded-full flex items-center justify-center text-white text-[10px] font-semibold">
                  5
                </span>
              </button>

              {/* Profile Avatar with Dropdown */}
              {user ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    className="rounded-full bg-gradient-to-br from-primary-700 to-primary-500 flex items-center justify-center text-white text-sm font-semibold border-2 border-white shadow-[0_2px_6px_rgba(0,0,0,0.04)]"
                    style={{ width: '42px', height: '42px' }}
                    aria-label="Profile"
                  >
                    {getInitials()}
                  </button>

                  {/* Profile Dropdown */}
                  {isProfileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-[#E3E8E5] py-1 z-50">
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-800 transition-colors duration-200 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Sign Out</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={handleSignIn}
                  className="px-4 py-2 bg-primary-700 text-white text-sm font-semibold rounded-full shadow-[0_2px_6px_rgba(0,0,0,0.04)] hover:bg-primary-500 transition-colors"
                  aria-label="Sign In"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </header>
      ) : (
        /* Desktop Header - Original Design */
        <header className="bg-white border-b border-airbnb-100 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              {/* Logo */}
              <div className="flex items-center">
                <Link to="/" className="flex items-center space-x-3 group">
                  <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
                    <span className="text-white font-bold text-xl">✈️</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold text-airbnb-800 group-hover:text-primary-500 transition-colors duration-200">
                      TripPlanner
                    </span>
                    <span className="text-xs text-airbnb-500 -mt-1">v{VERSION}</span>
                  </div>
                </Link>
              </div>

              {/* Navigation */}
              <nav className="hidden md:flex items-center space-x-2">
                <Link
                  to="/trips"
                  className={`nav-link ${
                    isActive('/trips') || isActive('/')
                      ? 'nav-link-active'
                      : 'text-airbnb-600 hover:text-airbnb-800'
                  }`}
                >
                  My Trips
                </Link>
                <button
                  onClick={() => navigate('/create-trip')}
                  className="nav-link text-airbnb-600 hover:text-airbnb-800"
                >
                  Create Trip
                </button>
              </nav>

              {/* User Menu */}
              <div className="flex items-center space-x-4">
                {user ? (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-airbnb-50 transition-colors duration-200"
                    >
                      <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center font-medium text-sm">
                        {getFirstName().charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-airbnb-800">
                        {getFirstName()}
                      </span>
                      <svg
                        className={`w-4 h-4 text-airbnb-600 transition-transform duration-200 ${
                          isProfileDropdownOpen ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {isProfileDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-airbnb-100 py-1 z-50">
                        <button
                          onClick={handleSignOut}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-800 transition-colors duration-200 flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span>Sign Out</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleSignIn}
                      className="btn-primary btn-sm"
                    >
                      Sign In
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
      )}
      
      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        mode={authMode}
      />
    </>
  );
};

export default Header;
